import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Callout, Icon, Text } from '@/nova/components';
import { useClipboard } from '@/nova/components/CopyButton';
import { ROUTE } from '@/nova/routes';
import type { StoryboardPromotion } from '@/store/slices/creativeJobs';

import type { AdSubject, AdSubjectType } from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import { downloadAsset } from '../../download-asset';
import { invalidateLandingDestinations } from '../../hooks/useLandingDestinations';
import { destinationHostPath, resolveCampaignDestination, siteOrigin, trackingLinkName } from '../../tracking-link';
import {
  LINK_FOR_PARAMS,
  type QuickLandingAnswer,
  type QuickLandingRequest,
  type TrackingLink as TrackingLinkRow,
  type TrackingLinkDestination,
  type TrackingLinkKind,
} from '../../tracking-links-api';
import { useTrackingLink } from '../../useTrackingLink';
import { LinkDestinationSheet, type DestinationChoice } from '../LinkDestinationSheet';
import { LumioMark } from '../LumioMark';

import { TrackingLinkStats } from './TrackingLinkStats';
import { useLandingBuild } from './useLandingBuild';
import {
  ActionRow,
  BuildCopy,
  BuildHint,
  BuildOpen,
  BuildRow,
  BuildTitle,
  ChangeBtn,
  CopyBtn,
  CopyField,
  CopyUrl,
  DownloadBtn,
  DownloadBtnText,
  FieldLabel,
  GoesTo,
  GoesToAction,
  GoesToLabel,
  GoesToMeta,
  GoesToPage,
  GoesToValue,
  LinkSkeleton,
  Root,
  SavedNote,
} from './TrackingLink.styles';

/** How long the "link created / moved" confirmation stays under the link. */
const SAVED_MS = 5000;

export interface TrackingLinkProps {
  /** The kind of asset the link belongs to. */
  kind: TrackingLinkKind;
  /** The asset's spark creatives id (for a landing page's own link: the page's id). */
  creativeId: string;
  /** The asset version the link is for (`versionKey(created_at)`; a landing page's own link: `page`). */
  versionKey: string;
  /** The asset's promotions, in play order; the lead one's banner drives the casino destination. */
  promotions: StoryboardPromotion[];
  /** The brand's creative pool, carrying each banner's on-site `cta_href`. */
  poolItems: CreativeItem[];
  /** The active brand's public domain, for an absolute casino destination. */
  brandDomain?: string;
  /** The asset's title, used in the link's name. */
  adName?: string;
  /** The finished asset's file url, for the inline Download button (beside the tracking action). */
  videoUrl?: string;
  /** The download filename. */
  downloadName?: string;
  /** The Download button's label, ALREADY TRANSLATED by the call site (its literal is what the extractor harvests,
   *  e.g. a kit's "Download ZIP"); rendered raw. Omitted = "Download video". */
  downloadLabel?: string;
  /** The brand (creatives tenant) id. */
  tenantId?: number;
  /** Let the owner decide where the link sends people (the destination sheet: the casino, one of the brand's landing
   *  pages, or, for an asset, a quick or a custom landing page). The choice lives on the version's link, so a
   *  reopened link shows it ("Goes to: …" with "Change"). */
  pickDestination?: boolean;
  /** A landing page's OWN link: it always lands on that page (a link pointing elsewhere is moved there once). No
   *  sheet. */
  fixedLandingPage?: { id: string; url: string };
  /** What the asset advertises: enables "Quick landing page". Omit when the asset has none. */
  subject?: { type: AdSubjectType; subject: AdSubject };
  /** Told the link as shown (null while there is none, or while it lands on a landing page that is not live), e.g.
   *  to use it as an exported ad's click-through. */
  onLinkChange?: (link: TrackingLinkState | null) => void;
  /** Open Buy credits with what is missing (a quick landing page the balance does not cover). Absent: an inline
   *  notice. */
  onBuyCredits?: (needed?: number) => void;
}

/** A shown tracking link: where it lands, the link itself, and whether it lands on a landing page rather than the
 *  casino. */
export interface TrackingLinkState {
  landingUrl?: string;
  trackingUrl?: string;
  landingPage: boolean;
}

/** The link's default name when the asset has no title (stored with the link; literal `t()` calls for the
 *  extractor). */
const useDefaultLinkName = (kind: TrackingLinkKind): string => {
  const { t } = useTranslation();
  switch (kind) {
    case 'kit':
      return t('Banner kit');
    case 'playable':
      return t('Mini-game');
    case 'banner':
      return t('HTML5 banner');
    case 'landing_page':
      return t('Landing page');
    default:
      return t('Video ad');
  }
};

/** Whether a link lands on a Studio landing page that is not live (visitors would reach the casino root, untracked). */
const landsOffline = (link: TrackingLinkRow): boolean =>
  link.destination_type === 'landing_page' && !!link.landing_page && link.landing_page.state !== 'live';

/** Whether a link lands on a Studio landing page that was taken down or failed to publish (not one still being made). */
const landsUnpublished = (link: TrackingLinkRow): boolean =>
  link.destination_type === 'landing_page' &&
  (link.landing_page?.state === 'unpublished' || link.landing_page?.state === 'failed');

/**
 * An asset version's tracking link (the composer's output pane), minted and stored by spark-creatives: the
 * destination plus `?aff_id=<affiliate>_<campaign>_<site>`, which signup splits into the player's attribution and the
 * Owners Hub Stats report on. On mount it shows the version's existing link straight away; a version without one shows
 * "Get tracking link". A Download button sits in the same row, so the finished file is one click away whatever the
 * link's state.
 *
 * Where the link lands: the casino by default (the lead promotion's page, else the homepage). With `pickDestination`,
 * "Get tracking link" opens the destination sheet (the casino, one of the brand's landing pages, a quick landing page
 * Lumio builds and publishes, or a custom one designed in the landing page composer); once a link is shown, a "Goes
 * to: …" line names where it lands (a Studio page links to its composer) with "Change", which reopens the sheet and
 * moves the same link (its `aff_id` stays). With `fixedLandingPage`, always that page. Under the link: its last 30
 * days of results.
 */
export const TrackingLink = ({
  kind,
  creativeId,
  versionKey,
  promotions,
  poolItems,
  brandDomain,
  adName,
  videoUrl,
  downloadName,
  downloadLabel,
  tenantId,
  pickDestination = false,
  fixedLandingPage,
  subject,
  onLinkChange,
  onBuyCredits,
}: TrackingLinkProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const picking = pickDestination && !fixedLandingPage;
  /** The landing page's own link (not an ad's link landing on it). */
  const ownPageLink = kind === 'landing_page';
  const offerLanding = picking && kind !== 'landing_page';
  const name = trackingLinkName(adName, promotions, versionKey, useDefaultLinkName(kind));
  const { phase, create, retarget, reload, adopt } = useTrackingLink({ tenantId, creativeId, versionKey, kind, name });
  const link = phase.kind === 'done' ? phase.link : null;
  // Shared clipboard state: async write with a non-secure-context (execCommand) fallback and a mounted-guarded
  // confirmation hold, so the copy works on a LAN-IP dev box / embedded webview too.
  const { copied, copy } = useClipboard();

  const [sheetOpen, setSheetOpen] = React.useState(false);
  // "Get tracking link" with nowhere to send it (no destination sheet, and the brand has no website address yet).
  const [noDestination, setNoDestination] = React.useState(false);
  // The quick landing page started from this panel (its page and run), watched until it is live.
  const [quick, setQuick] = React.useState<{ pageId: string; jobId?: string } | null>(null);

  // The casino destination: the lead promotion's page on the brand's website, else its homepage.
  const casinoUrl = resolveCampaignDestination(promotions, poolItems, brandDomain);
  const casino = casinoUrl ? { url: casinoUrl, promotionPage: casinoUrl !== siteOrigin(brandDomain) } : undefined;

  /** Where a choice sends the link (null: the casino has no address, the brand has no domain yet). */
  const destinationFor = (choice: DestinationChoice | undefined): TrackingLinkDestination | null => {
    if (fixedLandingPage) {
      return {
        destination_type: 'landing_page',
        landing_page_id: fixedLandingPage.id,
        destination_url: fixedLandingPage.url,
      };
    }
    if (choice?.kind === 'landing') {
      return {
        destination_type: 'landing_page',
        ...(choice.page.landingPageId ? { landing_page_id: choice.page.landingPageId } : {}),
        destination_url: choice.page.url,
      };
    }
    return casinoUrl ? { destination_type: 'casino', destination_url: casinoUrl } : null;
  };

  const getLink = (): void => {
    if (phase.kind === 'working') return;
    if (picking) {
      setSheetOpen(true);
      return;
    }
    const destination = destinationFor(undefined);
    setNoDestination(!destination);
    if (destination) void create(destination);
  };

  // The sheet's casino / landing page choice: make the link there (first time) or move the shown one (same aff_id).
  const sendTo = async (choice: DestinationChoice): Promise<boolean> => {
    const destination = destinationFor(choice);
    if (!destination) return false;
    const moving = phase.kind === 'done';
    const ok = moving ? await retarget(destination) : (await create(destination)) !== null;
    // Moved off a quick landing page started here: stop watching it.
    if (ok) {
      setQuick((cur) => (cur && choice.kind === 'landing' && choice.page.landingPageId === cur.pageId ? cur : null));
      // Say it worked, under the link (the sheet closes on its own).
      const where = choice.kind === 'landing' ? choice.page.name?.trim() || destinationHostPath(choice.page.url) : '';
      setSaved({ created: !moving, where, at: Date.now() });
    }
    return ok;
  };

  // The confirmation of the last create / move, shown for a few seconds.
  const [saved, setSaved] = React.useState<{ created: boolean; where: string; at: number } | null>(null);
  // Syncs with a timer: the confirmation fades after SAVED_MS.
  React.useEffect(() => {
    if (!saved) return undefined;
    const id = window.setTimeout(() => setSaved(null), SAVED_MS);
    return () => window.clearTimeout(id);
  }, [saved]);
  const savedNote = saved ? (
    <SavedNote key={saved.at} role="status">
      <Icon name="check" size={14} />
      {saved.created ? (
        saved.where ? (
          <Text
            tag="span"
            text="Tracking link created. It sends people to {{where}}."
            placeholders={{ where: saved.where }}
          />
        ) : (
          <Text tag="span" text="Tracking link created. It sends people to your website." />
        )
      ) : saved.where ? (
        <Text tag="span" text="Done. The link now sends people to {{where}}." placeholders={{ where: saved.where }} />
      ) : (
        <Text tag="span" text="Done. The link now sends people to your website." />
      )}
    </SavedNote>
  ) : null;

  // "Design a custom landing page": the full composer with the asset's context; once the page exists, the composer
  // points this version's link at it and offers the way back.
  const openComposer = (): void => {
    const qs = new URLSearchParams();
    qs.set(LINK_FOR_PARAMS.creativeId, creativeId);
    qs.set(LINK_FOR_PARAMS.versionKey, versionKey);
    qs.set(LINK_FOR_PARAMS.kind, kind);
    qs.set(LINK_FOR_PARAMS.name, name);
    if (subject?.subject.id) {
      qs.set(LINK_FOR_PARAMS.subjectType, subject.type);
      qs.set(LINK_FOR_PARAMS.subjectId, subject.subject.id);
    }
    qs.set(LINK_FOR_PARAMS.returnTo, `${location.pathname}${location.search}`);
    navigate(`${ROUTE.creativesLandingPageNew()}?${qs.toString()}`);
  };

  const quickRequest: QuickLandingRequest | undefined =
    offerLanding && subject
      ? {
          creative_id: creativeId,
          version_key: versionKey,
          kind,
          link_name: name,
          subject_type: subject.type,
          subject: subject.subject,
        }
      : undefined;

  // A landing page's own link that lands elsewhere is moved onto the page, once per page (a failed move is not
  // retried in a loop; the link keeps its aff_id either way).
  const fixedTriedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Syncs the stored link with the page it belongs to.
    if (!fixedLandingPage || !link || link.landing_page_id === fixedLandingPage.id) return;
    const attempt = `${link.id}|${fixedLandingPage.id}`;
    if (fixedTriedRef.current === attempt) return;
    fixedTriedRef.current = attempt;
    void retarget({
      destination_type: 'landing_page',
      landing_page_id: fixedLandingPage.id,
      destination_url: fixedLandingPage.url,
    });
  }, [fixedLandingPage, link, retarget]);

  // A quick landing page answered: its link already points at the new page; watch the page until it is live.
  const onQuickStarted = (answer: QuickLandingAnswer): void => {
    const page = answer.landing_page;
    if (answer.link) adopt(answer.link);
    else void reload();
    if (!page?.id) return;
    setQuick({ pageId: page.id, ...(answer.job_id ? { jobId: answer.job_id } : {}) });
  };

  // The page watched until it is live: the quick landing page started here, else a link that lands on a Studio page
  // never published (a quick landing page still building, or a page being designed in the composer).
  const buildPageId =
    quick?.pageId ?? (link?.landing_page?.state === 'draft' && link.landing_page_id ? link.landing_page_id : undefined);
  const startedHere = !!quick && quick.pageId === buildPageId;
  const build = useLandingBuild(tenantId, buildPageId, startedHere, startedHere ? quick?.jobId : undefined);

  // The page went live: read the link again (for its address); the brand's pages are read anew by the next sheet.
  const liveHandledRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Syncs the link and the shared landing page list with a page that just went live.
    if (build.kind !== 'live' || !buildPageId || liveHandledRef.current === buildPageId) return;
    liveHandledRef.current = buildPageId;
    if (tenantId) invalidateLandingDestinations(tenantId);
    const pageId = buildPageId;
    void reload().then(() => {
      setQuick((cur) => (cur?.pageId === pageId ? null : cur));
    });
  }, [build.kind, buildPageId, tenantId, reload]);

  // Report the link as shown (e.g. for an exported ad's click-through). A link on a landing page that is not live is
  // not offered: its visitors would land on the casino root without the link's tracking.
  const onLinkChangeRef = React.useRef(onLinkChange);
  onLinkChangeRef.current = onLinkChange;
  const usable = link && link.url && !landsOffline(link) ? link : null;
  const shownTrackingUrl = usable?.url;
  const shownLandingUrl = usable ? usable.landing_page?.url || usable.destination_url : undefined;
  const shownOnLanding = usable?.destination_type === 'landing_page';
  React.useEffect(() => {
    // Tells the parent (an external consumer) the link as shown.
    onLinkChangeRef.current?.(
      shownTrackingUrl
        ? { landingUrl: shownLandingUrl, trackingUrl: shownTrackingUrl, landingPage: shownOnLanding }
        : null,
    );
  }, [shownTrackingUrl, shownLandingUrl, shownOnLanding]);

  // The page went live but the link is not read again yet.
  const settling = build.kind === 'live' && !!link && landsOffline(link);
  const building = build.kind === 'building' || (settling && startedHere);
  // A reopened page's first read, or its link being read again: the placeholder holds (never a build view flashed
  // over a page that is only being designed).
  const waiting = build.kind === 'checking' || (settling && !startedHere);
  const busy = phase.kind === 'working' || building;

  const downloadEl = videoUrl ? (
    <DownloadBtn
      type="button"
      onClick={() => void downloadAsset(videoUrl, downloadName || 'video.mp4')}
      title={downloadLabel ?? t('Download video')}
    >
      <Icon name="download" size={16} />
      {downloadLabel ? <DownloadBtnText raw text={downloadLabel} /> : <DownloadBtnText text="Download video" />}
    </DownloadBtn>
  ) : null;
  const downloadRow = downloadEl ? <ActionRow>{downloadEl}</ActionRow> : null;

  // A landing page's own link is named apart from the ads' links that land on it (literal copy per branch).
  const fieldLabel = ownPageLink ? <FieldLabel text="Unique tracking link" /> : <FieldLabel text="Tracking link" />;
  const skeletonRow = (
    <>
      {fieldLabel}
      <ActionRow>
        <LinkSkeleton aria-hidden="true" />
        {downloadEl}
      </ActionRow>
    </>
  );

  // Still looking up the link: a subtle placeholder (+ download), so the button never flashes before we know whether
  // a link exists.
  if (phase.kind === 'loading') return <Root>{skeletonRow}</Root>;

  const openPageInComposer = (pageId: string): React.ReactElement => (
    <Button variant="text" label={t('Open in composer')} onClick={() => navigate(ROUTE.creativesLandingPage(pageId))} />
  );

  /** "Goes to: <casino | page name · host/path>" with "Change" (a Studio page's name opens its composer). */
  const goesTo = (shown: TrackingLinkRow): React.ReactElement | null => {
    if (!picking) return null;
    const studioId = shown.destination_type === 'landing_page' ? shown.landing_page_id : '';
    const where = destinationHostPath(shown.landing_page?.url || shown.destination_url);
    const pageName = shown.landing_page?.name.trim() ?? '';
    // A page still being designed (no Lumio run drawing it): not a warning, a page to finish and publish.
    const designing = shown.landing_page?.state === 'draft' && build.kind === 'designing';
    const pageHref = studioId ? ROUTE.creativesLandingPage(studioId) : '';
    return (
      <GoesTo>
        <GoesToLabel text="Goes to:" />
        {shown.destination_type === 'casino' ? (
          <>
            <GoesToValue text="Casino" />
            {where ? <GoesToMeta raw text={where} /> : null}
          </>
        ) : studioId ? (
          <>
            <GoesToPage to={pageHref} title={t('Open the landing page')}>
              {pageName ? <Text raw text={pageName} /> : <Text text="Landing page" />}
            </GoesToPage>
            {designing ? <GoesToMeta text="being designed" /> : where ? <GoesToMeta raw text={where} /> : null}
          </>
        ) : (
          <GoesToValue raw text={where} />
        )}
        {designing && pageHref ? (
          <GoesToAction to={pageHref}>
            <Text text="Open the landing page" />
          </GoesToAction>
        ) : null}
        <ChangeBtn type="button" onClick={() => setSheetOpen(true)} disabled={busy}>
          <Text text="Change" />
        </ChangeBtn>
      </GoesTo>
    );
  };

  const body = ((): React.ReactNode => {
    switch (phase.kind) {
      case 'no-brand':
        return (
          <>
            <Callout tone="warning" title={t('No brand selected')} text="Select a brand to create a tracking link." />
            {downloadRow}
          </>
        );
      case 'load-error':
        return (
          <>
            <Callout
              tone="danger"
              title={t('Could not load the tracking link')}
              text={phase.message || t('Please try again.')}
              action={<Button variant="text" label={t('Try again')} onClick={() => void reload()} />}
            />
            {downloadRow}
          </>
        );
      case 'error':
        return (
          <>
            <Callout
              tone="danger"
              title={t('Could not create the tracking link')}
              text={phase.message || t('Please try again.')}
              action={<Button variant="text" label={t('Try again')} onClick={getLink} />}
            />
            {downloadRow}
          </>
        );
      case 'done': {
        const shown = phase.link;
        const pageId = buildPageId ?? shown.landing_page_id;
        if (waiting) return skeletonRow;
        if (building) {
          return (
            <>
              <BuildRow role="status">
                {/* Lumio builds the page: the Lumio mark, never a sparkle. */}
                <LumioMark size={20} />
                <BuildCopy>
                  <BuildTitle text="Lumio is building your landing page" />
                  <BuildHint text="This takes about a minute or two. The link appears here once the page is live." />
                </BuildCopy>
                {pageId ? (
                  <BuildOpen to={ROUTE.creativesLandingPage(pageId)}>
                    <Text text="Open the landing page" />
                  </BuildOpen>
                ) : null}
              </BuildRow>
              {downloadRow}
            </>
          );
        }
        if (build.kind === 'failed' && pageId) {
          return (
            <>
              {goesTo(shown)}
              <Callout
                tone="danger"
                title={t('The landing page could not be finished')}
                text="Open it in the composer to finish and publish it, or pick another destination."
                action={openPageInComposer(pageId)}
              />
              {downloadRow}
            </>
          );
        }
        if (build.kind === 'slow' && pageId) {
          return (
            <>
              {goesTo(shown)}
              <Callout
                tone="info"
                title={t('Your landing page is taking longer than usual')}
                text="Open it in the composer to check on it."
                action={openPageInComposer(pageId)}
              />
              {downloadRow}
            </>
          );
        }
        return (
          <>
            {shown.url ? (
              <>
                {fieldLabel}
                <ActionRow>
                  <CopyField $flash={saved !== null}>
                    <CopyUrl
                      readOnly
                      value={shown.url}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label={ownPageLink ? t('Unique tracking link') : t('Tracking link')}
                    />
                    <CopyBtn
                      type="button"
                      $copied={copied}
                      onClick={() => void copy(shown.url)}
                      aria-label={copied ? t('Copied') : t('Copy link')}
                      title={copied ? t('Copied') : t('Copy link')}
                    >
                      <Icon name={copied ? 'check' : 'copy'} size={16} />
                    </CopyBtn>
                  </CopyField>
                  {downloadEl}
                </ActionRow>
                {goesTo(shown)}
                {savedNote}
              </>
            ) : (
              <>
                {goesTo(shown)}
                {savedNote}
                {downloadRow}
              </>
            )}
            {landsUnpublished(shown) ? (
              <Callout
                tone="warning"
                title={t('This landing page is not live')}
                text="Visitors who open this link land on the casino homepage and the link loses its tracking. Publish the page again, or pick another destination."
                action={shown.landing_page_id ? openPageInComposer(shown.landing_page_id) : undefined}
              />
            ) : null}
            {shown.campaign ? <TrackingLinkStats campaign={shown.campaign} /> : null}
          </>
        );
      }
      default:
        // idle / working: the first-time button, with Download beside it in the same row.
        return (
          <>
            {noDestination ? (
              <Callout
                tone="warning"
                title={t('Nowhere to send the link yet')}
                text="This brand has no website address yet. Pick a landing page, or try again once the website is set up."
              />
            ) : null}
            <ActionRow>
              <Button
                variant="outline"
                icon="external-link"
                label={
                  phase.kind === 'working'
                    ? t('Creating link…')
                    : ownPageLink
                      ? t('Get unique tracking link')
                      : t('Get tracking link')
                }
                disabled={phase.kind === 'working'}
                onClick={getLink}
              />
              {downloadEl}
            </ActionRow>
          </>
        );
    }
  })();

  return (
    <Root>
      {body}
      {sheetOpen && picking ? (
        <LinkDestinationSheet
          tenantId={tenantId}
          casino={casino}
          link={link}
          onDestination={sendTo}
          quickRequest={quickRequest}
          onQuickStarted={onQuickStarted}
          onCustom={offerLanding ? openComposer : undefined}
          onBuyCredits={onBuyCredits}
          onClose={() => setSheetOpen(false)}
        />
      ) : null}
    </Root>
  );
};
